import { Show } from '../types/Show';

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * Validates all required fields for publishing a show
 * This includes all mandatory fields that must be filled before a show can be published or scheduled
 */
export const validateShowForPublish = (show: Show): ValidationResult => {
  const errors: ValidationError[] = [];

  // Basic required fields
  if (!show.name || show.name.trim() === '') {
    errors.push({
      field: 'name',
      message: 'Show name/title is required'
    });
  }

  if (!show.description || show.description.trim() === '') {
    errors.push({
      field: 'description',
      message: 'Show description is required'
    });
  }

  if (!show.originCountry || show.originCountry.trim() === '') {
    errors.push({
      field: 'originCountry',
      message: 'Origin country is required'
    });
  }

  // Genre, Trope, Tag validation
  if (!show.genre || show.genre.trim() === '') {
    errors.push({
      field: 'genre',
      message: 'At least one genre is required'
    });
  }

  if (!show.trope || show.trope.trim() === '') {
    errors.push({
      field: 'trope',
      message: 'At least one trope is required'
    });
  }

  if (!show.tag || show.tag.trim() === '') {
    errors.push({
      field: 'tag',
      message: 'At least one tag is required'
    });
  }

  // Metadata validation
  if (show.metadata) {
    // Language validation
    const languageMetadata = show.metadata.find(m => m.type === 'LANGUAGE');
    if (!languageMetadata || !languageMetadata.value || languageMetadata.value.trim() === '') {
      errors.push({
        field: 'language',
        message: 'Language is required'
      });
    }

    // Maturity Rating validation - compulsory field
    const maturityRatingMetadata = show.metadata.find(m => m.type === 'MATURITY_RATING');
    if (!maturityRatingMetadata || !maturityRatingMetadata.value || maturityRatingMetadata.value.trim() === '') {
      errors.push({
        field: 'maturityRating',
        message: 'Maturity rating is required'
      });
    }
  } else {
    errors.push({
      field: 'metadata',
      message: 'Show metadata is missing (language and maturity rating required)'
    });
  }

  // Show type validation
  if (!show.type) {
    errors.push({
      field: 'type',
      message: 'Show type is required'
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Formats validation errors into a user-friendly message
 */
export const formatValidationErrors = (errors: ValidationError[]): string => {
  if (errors.length === 0) return '';

  if (errors.length === 1) {
    return errors[0].message;
  }

  return `Please fill in the following required fields:\n${errors.map(e => `• ${e.message}`).join('\n')}`;
};

/**
 * Gets a summary of missing required fields
 */
export const getMissingFieldsSummary = (errors: ValidationError[]): string => {
  const fieldNames = errors.map(e => {
    switch (e.field) {
      case 'name': return 'Title';
      case 'description': return 'Description';
      case 'originCountry': return 'Origin Country';
      case 'genre': return 'Genres';
      case 'trope': return 'Tropes';
      case 'tag': return 'Tags';
      case 'language': return 'Language';
      case 'maturityRating': return 'Maturity Rating';
      case 'type': return 'Show Type';
      default: return e.field;
    }
  });

  return fieldNames.join(', ');
};
